import React, { useEffect, useState } from "react";
import { Button, Modal, Slider, Typography, theme } from "antd";
import { UiStickyScoreCard } from "../../../../ui";
import "./GcsScaleModal.css";

const { Text } = Typography;

/**
 * The three Glasgow Coma Scale components, with the same ranges and "NT"
 * (not testable) zero mark used by the physiotherapist's GCS scale.
 */
export const GCS_COMPONENTS = [
  { key: "eye_opening_response", label: "Eye Opening Response (E)", max: 4 },
  { key: "verbal_response", label: "Verbal Response (V)", max: 5 },
  { key: "motor_response", label: "Motor Response (M)", max: 6 },
];

const EMPTY_SCORES = {
  eye_opening_response: 0,
  verbal_response: 0,
  motor_response: 0,
};

const marksFor = (max) =>
  Array.from({ length: max + 1 }, (_, i) => i).reduce(
    (acc, value) => ({ ...acc, [value]: value === 0 ? "NT" : String(value) }),
    {}
  );

export const getGcsInterpretation = (score) => {
  if (score < 9) return "Severe Brain Injury";
  if (score < 13) return "Moderate Brain Injury";
  if (score < 15) return "Mild Brain Injury";
  return "Normal";
};

const GcsScaleModal = ({ open, setOpen, value, onApply }) => {
  const [scores, setScores] = useState(EMPTY_SCORES);
  const { token } = theme.useToken();

  // Reopening the modal restores the breakdown that produced the current
  // score, so an existing entry can be adjusted rather than re-entered.
  useEffect(() => {
    if (!open) return;
    setScores({ ...EMPTY_SCORES, ...(value || {}) });
  }, [open, value]);

  const total = GCS_COMPONENTS.reduce(
    (sum, component) => sum + (scores[component.key] || 0),
    0
  );
  const interpretation = getGcsInterpretation(total);

  const handleApply = () => {
    onApply?.({ ...scores, score: total, interpretation });
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      title="Glasgow Coma Scale"
      width={560}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={() => setOpen(false)}>
          Cancel
        </Button>,
        <Button key="apply" type="primary" onClick={handleApply}>
          Submit
        </Button>,
      ]}
    >
      <div className="forms-gcs-modal">
        <UiStickyScoreCard
          compact
          sticky={false}
          maxWidth={9999}
          className="forms-gcs-score-card"
          style={{
            background: `linear-gradient(180deg, ${token.colorPrimary} 0%, ${token.colorPrimaryHover} 100%)`,
            color: "#fff",
            width: "100%",
            maxWidth: "100%",
          }}
          title={interpretation}
          score={total}
          total={15}
        />
        {GCS_COMPONENTS.map((component) => (
          <div className="forms-gcs-card" key={component.key}>
            <Text className="forms-gcs-card-label">{component.label}</Text>
            <Slider
              marks={marksFor(component.max)}
              min={0}
              max={component.max}
              step={1}
              value={scores[component.key] || 0}
              onChange={(next) =>
                setScores((prev) => ({ ...prev, [component.key]: next }))
              }
              tooltip={{ open: false }}
            />
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default GcsScaleModal;
